import type { FC } from 'react'
import type { GridCellCtx, GridColumn, GridTreeData } from './types'
import type { GridTreeMeta } from './useTreeRows'
import { GUIDE_ORIGIN, GUIDE_STEP, treeGuideStyle } from './treeGuides'
import classes from './grid-theme.module.scss'

/** Chevron box width — also the gap a childless row leaves in its place. */
const CHEVRON_SIZE = 24
const CELL_PADDING = GUIDE_ORIGIN - CHEVRON_SIZE / 2

const Chevron: FC<{ expanded: boolean }> = ({ expanded }) => (
  <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
    <path
      d={expanded ? 'M1 3 L9 3 L5 8 Z' : 'M3 1 L8 5 L3 9 Z'}
      fill="currentColor"
    />
  </svg>
)

interface TreeGroupCellProps<T> {
  ctx: GridCellCtx<T>
  meta?: GridTreeMeta
  column: GridColumn<T>
  onToggle: (rowId: string, expanded: boolean) => void
}

const TreeGroupCell = <T,>({ ctx, meta, column, onToggle }: TreeGroupCellProps<T>) => {
  const depth = meta?.depth ?? 0
  const Renderer = column.cellRenderer

  return (
    <>
      {meta?.hasChildren ? (
        <button
          type="button"
          data-no-cell-edit
          className={classes.treeChevron}
          style={{ left: CELL_PADDING + depth * GUIDE_STEP }}
          aria-label={meta.expanded ? 'Collapse' : 'Expand'}
          aria-expanded={meta.expanded}
          onClick={(event) => {
            event.stopPropagation()
            onToggle(ctx.rowId, !meta.expanded)
          }}
        >
          <Chevron expanded={meta.expanded} />
        </button>
      ) : null}
      {Renderer ? <Renderer {...ctx} /> : ((ctx.value as React.ReactNode) ?? null)}
    </>
  )
}

interface BuildTreeGroupColumnArgs<T> {
  treeData: GridTreeData<T>
  metaById: Map<string, GridTreeMeta>
  onToggle: (rowId: string, expanded: boolean) => void
  isDark: boolean
}

/**
 * Wraps the page's group column so the grid owns the chevron, the per-level
 * indent and the connector guides, and the page's own renderer keeps drawing
 * only the value.
 *
 * The indent is applied as cell padding rather than an inner spacer so an open
 * editor lines up with the value it replaced — an inline element would sit
 * inside the cell's content box, which the editor takes over wholesale.
 */
export const buildTreeGroupColumn = <T,>({
  treeData,
  metaById,
  onToggle,
  isDark,
}: BuildTreeGroupColumnArgs<T>): GridColumn<T> => {
  const { groupColumn } = treeData
  const guideColor =
    treeData.guideColor ?? (isDark ? '#2b4a55' : '#cfdad8')
  const baseCellStyle = groupColumn.cellStyle

  return {
    ...groupColumn,
    cellClass: (ctx) => {
      const own =
        typeof groupColumn.cellClass === 'function'
          ? groupColumn.cellClass(ctx)
          : groupColumn.cellClass
      const list = Array.isArray(own) ? own : own ? [own] : []
      return [classes.treeGroupCell, ...list]
    },
    cellStyle: (ctx) => {
      const meta = metaById.get(ctx.rowId)
      const own =
        typeof baseCellStyle === 'function' ? baseCellStyle(ctx) : baseCellStyle
      return {
        paddingLeft:
          CELL_PADDING + (meta?.depth ?? 0) * GUIDE_STEP + CHEVRON_SIZE,
        ...(treeData.showGuides ? treeGuideStyle(meta, guideColor) : null),
        ...own,
      }
    },
    cellRenderer: (ctx: GridCellCtx<T>) => (
      <TreeGroupCell
        ctx={ctx}
        meta={metaById.get(ctx.rowId)}
        column={groupColumn}
        onToggle={onToggle}
      />
    ),
  }
}

export default TreeGroupCell
